import { useEffect, useMemo, useState } from "react";
import { Fuel, MapPin, TrendingDown, Trophy, Loader2, AlertCircle, ExternalLink, Wrench, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Bencinas Chile — CNE fuel price viewer.
 * Consumes the backend proxy /api/utilities/bencinas* which caches the CNE dataset for 60min.
 */

const fmtCLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(
    Math.round(n || 0),
  );

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "short", year: "numeric" }).format(d);
  } catch {
    return iso;
  }
};

// Consistent color per station based on relative price (green cheapest → red most expensive)
const priceColor = (price, min, max) => {
  if (!price || min === max) return "#8DDA23";
  const t = Math.max(0, Math.min(1, (price - min) / (max - min)));
  // interpolate: 0 (cheap) = green #0E6B2A ; 1 (expensive) = red #DC2626
  const r = Math.round(14 + t * (220 - 14));
  const g = Math.round(107 - t * (107 - 38));
  const b = Math.round(42 - t * (42 - 38));
  return `rgb(${r},${g},${b})`;
};

export default function BencinasCalculator() {
  useSeo({
    title: "Bencinas Chile · Precios de combustibles por comuna · DoSoft",
    description:
      "Consulta el precio de las bencinas (93/95/97/Diésel/GLP) en cada comuna de Chile con mapa, top marcas y ranking. Datos oficiales de la CNE actualizados diariamente.",
    canonical: "/utilidades/bencinas",
  });

  // noindex — internal tool.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  const [meta, setMeta] = useState(null);
  const [region, setRegion] = useState("");
  const [comuna, setComuna] = useState("");
  const [fuel, setFuel] = useState("93");
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStations, setLoadingStations] = useState(false);

  // Bootstrap: load meta first + geolocate defaults
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get("/utilities/bencinas/meta")
      .then(({ data }) => {
        if (cancel) return;
        setMeta(data);
        // Default to RM 13 · Santiago 13101 if available
        const rm = data.regions.find((r) => r.code === "13") || data.regions[0];
        if (rm) {
          setRegion(rm.code);
          const stgo = rm.comunas.find((c) => c.code === "13101") || rm.comunas[0];
          if (stgo) setComuna(stgo.code);
        }
      })
      .catch((e) => setError(e?.message || "No se pudo cargar la información."))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  const currentRegion = useMemo(
    () => meta?.regions.find((r) => r.code === region),
    [meta, region],
  );

  const comunaObj = useMemo(
    () => currentRegion?.comunas.find((c) => c.code === comuna),
    [currentRegion, comuna],
  );

  // Reset comuna when region changes and current comuna is not part of that region
  useEffect(() => {
    if (!currentRegion) return;
    if (!currentRegion.comunas.some((c) => c.code === comuna)) {
      setComuna(currentRegion.comunas[0]?.code || "");
    }
  }, [region]);

  // Fetch stations when filters change
  useEffect(() => {
    if (!region || !comuna || !fuel) return;
    let cancel = false;
    const ctl = new AbortController();
    setLoadingStations(true);
    api
      .get("/utilities/bencinas", {
        params: { region, comuna, combustible: fuel },
        signal: ctl.signal,
      })
      .then(({ data }) => {
        if (cancel) return;
        setStations(data.stations || []);
      })
      .catch(() => !cancel && setStations([]))
      .finally(() => !cancel && setLoadingStations(false));
    return () => {
      cancel = true;
      ctl.abort();
    };
  }, [region, comuna, fuel]);

  // Analytics
  const analytics = useMemo(() => {
    if (!stations.length) return null;
    const prices = stations.map((s) => s.prices[fuel]?.price).filter((p) => p > 0);
    if (!prices.length) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    const cheapest = stations.find((s) => s.prices[fuel]?.price === min);
    const mostExpensive = stations.find((s) => s.prices[fuel]?.price === max);
    // by brand
    const brandMap = new Map();
    for (const s of stations) {
      const p = s.prices[fuel]?.price;
      if (!p) continue;
      const b = s.brand || "Otras";
      if (!brandMap.has(b)) brandMap.set(b, { brand: b, logo: s.logo, count: 0, sum: 0, min: p, max: p });
      const entry = brandMap.get(b);
      entry.count += 1;
      entry.sum += p;
      entry.min = Math.min(entry.min, p);
      entry.max = Math.max(entry.max, p);
    }
    const byBrand = [...brandMap.values()]
      .map((b) => ({ ...b, avg: b.sum / b.count }))
      .sort((a, b) => a.avg - b.avg);
    return { min, max, avg, cheapest, mostExpensive, byBrand, prices };
  }, [stations, fuel]);

  const fuelDef = meta?.fuels.find((f) => f.code === fuel);
  const stationsWithCoords = stations.filter((s) => s.lat && s.lon);
  const mapCenter = stationsWithCoords.length
    ? [stationsWithCoords[0].lat, stationsWithCoords[0].lon]
    : [-33.45, -70.66];

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900 flex items-center gap-3 flex-wrap">
            Bencinas Chile
            <span className="text-xs md:text-sm font-mono font-normal bg-brand/15 text-brand-active px-2 py-1 rounded uppercase tracking-widest">
              CNE · Oficial
            </span>
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Compara precios de combustible en tu comuna con datos oficiales de la Comisión Nacional de Energía. Encuentra la estación más barata cerca de ti.
          </p>
          {meta && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-ink-800/60">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand/10 text-brand-active">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0E6B2A] animate-pulse" />
                EN VIVO
              </span>
              <span>
                Fuente:{" "}
                <a href="https://www.cne.cl" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand">
                  {meta.source}
                </a>
              </span>
              <span>· {meta.total_stations} estaciones en {meta.regions.length} regiones</span>
              {meta.last_update && <span>· Actualizado {fmtDate(meta.last_update)}</span>}
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-3 p-6 rounded-2xl bg-white border border-ash-100/80 text-ink-800/70">
            <Loader2 className="animate-spin" size={18} /> Cargando red de estaciones desde la CNE…
          </div>
        )}

        {error && !loading && (
          <div className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-red-200 text-red-700">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">No se pudo cargar el catálogo de estaciones</div>
              <div className="text-xs opacity-80 mt-1">{error}</div>
            </div>
          </div>
        )}

        {!loading && !error && meta && (
          <>
            {/* Filters */}
            <section className="rounded-2xl overflow-hidden border border-ink-500 bg-white shadow-lg mb-6">
              <div className="bg-ink-900 text-ash-100 px-5 py-4">
                <div className="text-[10px] uppercase tracking-widest font-mono text-ash-500 mb-1">Buscar precios</div>
                <div className="text-sm text-ash-100/90">Elige tu región, comuna y tipo de combustible.</div>
              </div>
              <div className="p-5 md:p-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">Región</div>
                  <select
                    data-testid="bencinas-region-select"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="w-full h-11 border border-ink-500/30 hover:border-brand focus:border-brand focus:outline-none rounded-lg px-3 bg-white text-sm text-ink-900 font-mono"
                  >
                    {meta.regions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">Comuna</div>
                  <select
                    data-testid="bencinas-comuna-select"
                    value={comuna}
                    onChange={(e) => setComuna(e.target.value)}
                    className="w-full h-11 border border-ink-500/30 hover:border-brand focus:border-brand focus:outline-none rounded-lg px-3 bg-white text-sm text-ink-900 font-mono"
                  >
                    {(currentRegion?.comunas || []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.count})
                      </option>
                    ))}
                  </select>
                </label>
                <div className="md:col-span-2">
                  <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-2">
                    Combustible
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {meta.fuels.map((f) => (
                      <button
                        key={f.code}
                        data-testid={`bencinas-fuel-${f.code}`}
                        onClick={() => setFuel(f.code)}
                        className={`px-3 h-10 rounded-lg text-xs uppercase tracking-widest font-mono transition-all border ${
                          fuel === f.code
                            ? "bg-brand text-ink-900 border-brand shadow"
                            : "border-ink-500/25 hover:border-brand hover:text-brand text-ink-800"
                        }`}
                      >
                        <Fuel size={12} className="inline mr-1.5 -mt-0.5" strokeWidth={2} />
                        {f.short}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {loadingStations && (
              <div className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-ash-100/80 text-ink-800/70 mb-6">
                <Loader2 className="animate-spin" size={18} /> Buscando estaciones…
              </div>
            )}

            {!loadingStations && stations.length === 0 && (
              <div className="p-5 rounded-2xl bg-white border border-ash-100/80 text-ink-800/70 text-sm">
                No hay estaciones con datos de <b>{fuelDef?.label}</b> en {comunaObj?.name}. Prueba otro combustible o cambia de comuna.
              </div>
            )}

            {!loadingStations && analytics && (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <Kpi
                    label="La más barata"
                    value={fmtCLP(analytics.min)}
                    sublabel={`${analytics.cheapest?.brand}`}
                    accent="down"
                  />
                  <Kpi
                    label="Promedio"
                    value={fmtCLP(analytics.avg)}
                    sublabel={`${analytics.prices.length} estaciones`}
                  />
                  <Kpi
                    label="La más cara"
                    value={fmtCLP(analytics.max)}
                    sublabel={analytics.mostExpensive?.brand}
                    accent="up"
                  />
                  <Kpi
                    label="Ahorro potencial"
                    value={fmtCLP(analytics.max - analytics.min)}
                    sublabel="cara vs barata"
                    accent={analytics.max - analytics.min > 50 ? "down" : undefined}
                  />
                </div>

                {/* Cheapest highlight card */}
                <section className="rounded-2xl overflow-hidden border border-brand bg-gradient-to-br from-brand/5 to-white mb-6" data-testid="bencinas-cheapest-card">
                  <div className="p-5 md:p-6 flex flex-wrap items-center gap-4 md:gap-6">
                    <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center text-ink-900 shrink-0">
                      <Trophy size={26} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-[10px] uppercase tracking-widest font-mono text-brand-active font-bold mb-1">
                        La más barata en {comunaObj?.name}
                      </div>
                      <div className="font-display font-black text-2xl md:text-3xl text-ink-900 leading-tight">
                        {analytics.cheapest?.brand}
                      </div>
                      <div className="text-sm text-ink-800/70 mt-0.5 flex items-start gap-1">
                        <MapPin size={13} className="mt-1 shrink-0 text-brand" />
                        <span>{analytics.cheapest?.address}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest font-mono text-ink-800/60 mb-1">
                        {fuelDef?.short}
                      </div>
                      <div className="font-display font-black text-3xl md:text-4xl text-brand-active leading-none">
                        {fmtCLP(analytics.min)}
                      </div>
                      <div className="text-[11px] font-mono text-ink-800/50 mt-1">{fuelDef?.unit}</div>
                    </div>
                    {analytics.cheapest?.lat && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${analytics.cheapest.lat},${analytics.cheapest.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-[11px] uppercase tracking-widest font-mono bg-ink-900 text-ash-100 hover:bg-brand hover:text-ink-900 px-4 h-10 rounded-lg inline-flex items-center gap-2 transition-colors"
                        data-testid="bencinas-cheapest-nav"
                      >
                        Ir <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </section>

                {/* Map */}
                {stationsWithCoords.length > 0 && (
                  <section className="rounded-2xl overflow-hidden border border-ash-100/80 bg-white shadow-sm mb-6">
                    <div className="px-5 py-4 border-b border-ash-100/70 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-brand" strokeWidth={2} />
                        <h2 className="font-display font-bold text-ink-900">Mapa de estaciones</h2>
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-800/50">
                        Verde: más barata · Rojo: más cara
                      </div>
                    </div>
                    <div style={{ height: 420 }} data-testid="bencinas-map">
                      <MapContainer
                        center={mapCenter}
                        zoom={13}
                        style={{ height: "100%", width: "100%" }}
                        key={`${region}-${comuna}-${fuel}`}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {stationsWithCoords.map((s) => {
                          const p = s.prices[fuel]?.price;
                          const color = priceColor(p, analytics.min, analytics.max);
                          const isCheapest = p === analytics.min;
                          return (
                            <CircleMarker
                              key={s.id}
                              center={[s.lat, s.lon]}
                              radius={isCheapest ? 12 : 8}
                              pathOptions={{
                                color: isCheapest ? "#0E6B2A" : color,
                                fillColor: color,
                                fillOpacity: 0.85,
                                weight: isCheapest ? 3 : 1,
                              }}
                            >
                              <Popup>
                                <div style={{ fontFamily: "system-ui", minWidth: 180 }}>
                                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.brand}</div>
                                  <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2 }}>{s.address}</div>
                                  <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700, color: isCheapest ? "#0E6B2A" : "#111" }}>
                                    {fmtCLP(p)} {fuelDef?.unit}
                                  </div>
                                  {isCheapest && (
                                    <div style={{ marginTop: 3, fontSize: 10, color: "#0E6B2A", fontWeight: 600 }}>
                                      ⭐ La más barata
                                    </div>
                                  )}
                                  <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: "inline-block", marginTop: 6, fontSize: 11, color: "#0e6b2a", fontWeight: 600 }}
                                  >
                                    Cómo llegar →
                                  </a>
                                </div>
                              </Popup>
                            </CircleMarker>
                          );
                        })}
                      </MapContainer>
                    </div>
                  </section>
                )}

                {/* By Brand */}
                {analytics.byBrand.length > 1 && (
                  <section className="rounded-2xl overflow-hidden border border-ash-100/80 bg-white shadow-sm mb-6">
                    <div className="px-5 py-4 border-b border-ash-100/70 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-brand" strokeWidth={2} />
                        <h2 className="font-display font-bold text-ink-900">Ranking por marca</h2>
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-800/50">
                        promedio en {comunaObj?.name}
                      </div>
                    </div>
                    <div className="divide-y divide-ash-100/60" data-testid="bencinas-brand-ranking">
                      {analytics.byBrand.map((b, i) => (
                        <div key={b.brand} className="flex items-center gap-4 px-5 py-3">
                          <div className={`w-7 text-center font-mono text-xs ${i === 0 ? "text-brand-active font-bold" : "text-ink-800/40"}`}>
                            {i + 1}
                          </div>
                          {b.logo ? (
                            <img src={b.logo} alt={b.brand} className="w-9 h-9 object-contain rounded bg-white border border-ash-100/50" onError={(e) => e.target.style.display = 'none'} />
                          ) : (
                            <div className="w-9 h-9 rounded bg-ash-100 border border-ash-100/50" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-ink-900 truncate">{b.brand}</div>
                            <div className="text-[11px] text-ink-800/50 font-mono">
                              {b.count} {b.count === 1 ? "estación" : "estaciones"} · rango {fmtCLP(b.min)}–{fmtCLP(b.max)}
                            </div>
                          </div>
                          <div className={`font-display font-black text-lg leading-none ${i === 0 ? "text-brand-active" : "text-ink-900"}`}>
                            {fmtCLP(b.avg)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Full station table */}
                <section className="rounded-2xl overflow-hidden border border-ash-100/80 bg-white shadow-sm mb-6">
                  <div className="px-5 py-4 border-b border-ash-100/70 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={16} className="text-brand" strokeWidth={2} />
                      <h2 className="font-display font-bold text-ink-900">Todas las estaciones</h2>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-800/50">
                      {stations.length} estaciones · ordenado por precio
                    </div>
                  </div>
                  <div className="overflow-x-auto" data-testid="bencinas-table">
                    <table className="w-full text-sm">
                      <thead className="bg-ash-100/60 text-ink-800 text-xs">
                        <tr>
                          <th className="text-left px-4 py-3 font-mono uppercase tracking-widest">#</th>
                          <th className="text-left px-4 py-3 font-mono uppercase tracking-widest">Marca</th>
                          <th className="text-left px-4 py-3 font-mono uppercase tracking-widest">Dirección</th>
                          <th className="text-right px-4 py-3 font-mono uppercase tracking-widest">Precio</th>
                          <th className="text-right px-4 py-3 font-mono uppercase tracking-widest">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stations.map((s, i) => {
                          const p = s.prices[fuel];
                          const isCheapest = p?.price === analytics.min;
                          return (
                            <tr key={s.id} className={`border-t border-ash-100/50 ${isCheapest ? "bg-brand/5" : ""}`}>
                              <td className="px-4 py-3 font-mono text-xs text-ink-800/60">
                                {isCheapest ? (
                                  <span className="text-brand-active font-bold">🏆</span>
                                ) : (
                                  i + 1
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  {s.logo ? (
                                    <img src={s.logo} alt={s.brand} className="w-7 h-7 object-contain rounded bg-white border border-ash-100/40" onError={(e) => e.target.style.display = 'none'} />
                                  ) : null}
                                  <span className="font-semibold text-ink-900">{s.brand}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-ink-800/80">{s.address}</td>
                              <td className={`px-4 py-3 text-right font-display font-black ${isCheapest ? "text-brand-active" : "text-ink-900"}`}>
                                {fmtCLP(p?.price)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {s.lat && s.lon && (
                                  <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] uppercase tracking-widest font-mono border border-ink-500/25 hover:border-brand hover:text-brand text-ink-800 rounded px-2 py-1 inline-flex items-center gap-1 transition-colors"
                                  >
                                    Ir <ExternalLink size={10} />
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>

                <div className="text-[11px] text-ink-800/50 font-mono">
                  Los precios son informados por cada estación a la Comisión Nacional de Energía (CNE) y pueden variar en el momento de la compra. Fuente oficial:{" "}
                  <a href="https://www.bencinaenlinea.cl" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand">
                    bencinaenlinea.cl
                  </a>
                  .
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ label, value, sublabel, accent }) {
  const color =
    accent === "up" ? "text-red-600" : accent === "down" ? "text-[#0E6B2A]" : "text-ink-900";
  return (
    <div className="bg-white rounded-xl border border-ash-100/80 p-4">
      <div className="text-[10px] uppercase tracking-widest font-mono text-ink-800/60 mb-1">
        {label}
      </div>
      <div className={`font-display font-black text-lg md:text-xl leading-tight ${color}`}>{value}</div>
      {sublabel && (
        <div className="text-[11px] text-ink-800/50 font-mono mt-1 truncate">{sublabel}</div>
      )}
    </div>
  );
}
